import React, { useState, useEffect, useCallback } from 'react';
import update from 'immutability-helper';

import CButton from '../button/button';
import { Line, Space } from '../utils/utils';

import '../../theme/global.scss';
import CUpload from '../input/upload';
import { combineFiles, handleFileChange, initialFile } from '../../utils/files';
import {
  addAdditionalDocument,
  addFollowupRaport,
  addRaport,
  updateRaport
} from '../../network/raports';
import { notify } from '../../notifications/notification';
import CInput from '../input/input';
import {
  typeActionRaport,
  typesActionsRaports
} from '../../types/actions-raports';
import { mapRaportsTitle } from '../../utils/strings';
import { IRaportFullInfo } from '../../utils/interfaces/interfaces';
import DraggableCard from '../draggable-card/draggable-card';
import { useGetCurrentUserQuery } from '../../api';

interface IARaport {
  callback: () => void;
  raport?: IRaportFullInfo;
  type: typeActionRaport;
}

interface IDraggableCard {
  id: number;
  text: string;
}

const AddRaport = (props: IARaport) => {
  const [raport, setRaport] = useState(initialFile);
  const [aditionalDoc, setAditionalDoc] = useState(initialFile);
  const [name, setName] = useState(''); /* raport file name */
  const [aditionalDocumentName, setNameAditionalDoc] =
    useState(''); /* aditional document file name */
  const [loading, setLoading] = useState(false);

  const handleFile = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFile: any
  ) => {
    if (event.target.files && event.target.files[0].type) {
      if (event.target.files[0].type !== 'application/pdf') {
        notify('Trebuie să încărcați un document de tip PDF', 'info');
        return;
      }
    }

    if (event.target.files) {
      let files = event.target.files;
      let filesArray = Array.from(files);
      setCards(
        filesArray.map((file, index) => {
          return { text: file.name, id: index };
        })
      );
    }

    handleFileChange(event, setFile);
  };

  const [cards, setCards] = useState<IDraggableCard[]>([]);

  const moveCard = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const dragCard = cards[dragIndex];
      setCards(
        update(cards, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragCard]
          ]
        })
      );
    },
    [cards]
  );

  const renderCard = (card: { id: number; text: string }, index: number) => {
    return (
      <DraggableCard
        key={card.id}
        index={index}
        id={card.id}
        text={card.text}
        moveCard={moveCard}
      />
    );
  };

  const handleSubmit = async (event: React.MouseEvent) => {
    /* prevent from reloading */
    event.preventDefault();
    if (loading) {
      return;
    } else {
      setLoading(true);
    }

    const raportCombined = await combineFiles(raport, name, cards);
    const aditionalCombined = await combineFiles(
      aditionalDoc,
      aditionalDocumentName,
      cards
    );

    /* submit request */
    if (props.type === typesActionsRaports.ADD_RAPORT) {
      if (raport.length === 0) {
        notify('Nu ați introdus niciun raport', 'warning');
        setLoading(false);
        return;
      }

      if (name.length === 0) {
        notify('Nu ați introdus numele raportului', 'warning');
        setLoading(false);
        return;
      }

      addRaport(raportCombined, name, aditionalCombined, aditionalDocumentName)
        .then(() => {
          notify('Ați introdus cu succes un nou raport', 'success');
          props.callback();
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });

      return;
    }
    if (props.type === typesActionsRaports.ADD_FOLLOWUP) {
      if (raport.length === 0) {
        notify('Nu ați introdus niciun document', 'warning');
        setLoading(false);
        return;
      }

      if (name.length === 0) {
        notify('Nu ați introdus numele raportului', 'warning');
        setLoading(false);
        return;
      }

      if (props.raport) {
        addFollowupRaport(props.raport.id, raportCombined, name)
          .then(() => {
            notify(
              'Ați introdus cu succes un nou raport de followup',
              'success'
            );
            props.callback();
          })
          .catch(() => {
            setLoading(false);
          });
      }
      return;
    }
    if (props.type === typesActionsRaports.EDIT_RAPORT) {
      if (raport.length === 0) {
        notify('Nu ați introdus niciun document', 'warning');
        setLoading(false);
        return;
      }

      if (name.length === 0) {
        notify('Nu ați introdus numele raportului', 'warning');
        setLoading(false);
        return;
      }

      if (props.raport) {
        updateRaport(raportCombined, name, props.raport.id).then(() => {
          notify('Ați modificat cu succes raportul', 'success');
          props.callback();
        });
      }

      if (aditionalDocumentName && props.raport && aditionalDoc.length > 0) {
        addAdditionalDocument(
          aditionalCombined,
          aditionalDocumentName,
          props.raport.id
        )
          .then(() => {
            notify(
              'Ați introdus cu succes un nou document aditional',
              'success'
            );
            props.callback();
          })
          .catch(() => {
            setLoading(false);
          });
      }
      return;
    }

    if (props.type === typesActionsRaports.ADDITIONAL_DOCUMENT) {
      if (aditionalDoc.length === 0) {
        notify('Nu ați introdus niciun document', 'warning');
        setLoading(false);
        return;
      }

      if (aditionalDocumentName.length === 0) {
        notify('Nu ați introdus numele documentului', 'warning');
        setLoading(false);
        return;
      }

      if (props.raport) {
        addAdditionalDocument(
          aditionalCombined,
          aditionalDocumentName,
          props.raport.id
        )
          .then(() => {
            notify(
              'Ați introdus cu succes un nou document aditional',
              'success'
            );
            props.callback();
          })
          .catch(() => {
            setLoading(false);
          });
      }
      return;
    }
  };

  useEffect(() => {
    if (props.raport) {
      setName(props.raport.name);
    }
  }, [props.raport]);

  const { data: user } = useGetCurrentUserQuery();

  return (
    <form>
      <p className='body-dark bold'>
        {mapRaportsTitle(props.type, user?.institution.private)}
      </p>
      <Line width={'80px'} />
      <Space vertical={'30px'} />
      {(props.type === typesActionsRaports.ADD_RAPORT ||
        props.type === typesActionsRaports.EDIT_RAPORT ||
        props.type === typesActionsRaports.ADD_FOLLOWUP) && (
        <>
          <CInput
            label={
              !user?.institution.private
                ? 'Numele raportului'
                : 'Numele raportului / contractului'
            }
            type='text'
            id='name'
            placeholder={
              !user?.institution.private
                ? 'Numele raportului'
                : 'Numele raportului / contractului'
            }
            name='name'
            value={name}
            onChange={e => setName(e.target.value)}
            required
          />
          <CUpload
            label={'Fișier'}
            id='raport'
            placeholder='raport'
            name='raport'
            onChange={e => handleFile(e, setRaport)}
            required
            title={
              !user?.institution.private
                ? 'Încărcați un raport'
                : 'Încărcați un raport / contract'
            }
            value={raport}
          />
          <div>{cards.map((card, i) => renderCard(card, i))}</div>
        </>
      )}
      {(props.type === typesActionsRaports.ADD_RAPORT ||
        props.type === typesActionsRaports.EDIT_RAPORT) && (
        <CInput
          label='Numele documentului adițional (Optional)'
          type='text'
          id='decision_name'
          placeholder='Numele documentului'
          name='decision_name'
          value={aditionalDocumentName}
          onChange={e => setNameAditionalDoc(e.target.value)}
          required
        />
      )}
      {(props.type === typesActionsRaports.ADD_RAPORT ||
        props.type === typesActionsRaports.EDIT_RAPORT) && (
        <CUpload
          label={'Document adițional (Optional)'}
          id='decision'
          title='Optional'
          name='decision'
          value={aditionalDoc}
          onChange={e => handleFile(e, setAditionalDoc)}
        />
      )}

      {props.type === typesActionsRaports.ADDITIONAL_DOCUMENT && (
        <>
          <CInput
            label='Numele documentului adițional'
            type='text'
            id='decision_name'
            placeholder='Numele documentului'
            name='decision_name'
            value={aditionalDocumentName}
            onChange={e => setNameAditionalDoc(e.target.value)}
            required
          />

          <CUpload
            label={'Document adițional'}
            id='decision'
            title='Incarcati documentul'
            name='decision'
            value={aditionalDoc}
            onChange={e => handleFile(e, setAditionalDoc)}
          />

          <div>{cards.map((card, i) => renderCard(card, i))}</div>
        </>
      )}
      <Space vertical={'30px'} />
      <CButton label='Adaugă' type='submit' onClick={handleSubmit} />
    </form>
  );
};

export default AddRaport;
